<template>
  <div>
    <template v-if="simulations > 0">
      <vs-row vs-type="flex" vs-justify="space-between">
        <!-- Operating Systems -->
        <vs-col vs-w="2.5" vs-xs="0">
          <vx-card>
            <h5 class="mb-4 text-center">Operating Systems</h5>
            <template v-if="operatingSystems.data.length > 0">
              <pie-chart
                style="height: 200px;"
                :chart-data="operatingSystems.data"
                chart-name="Operating System"
                :key="CHART_KEYS.OS_CHART_KEY"
              ></pie-chart>
              <p
                class="text-center text-muted"
                style="font-size: 8px;"
              >*Limited to top five documents based on your simulations statistics.</p>
            </template>
            <template v-else>
              <div class="block mx-auto my-3 text-center" style="height: 210px;">
                <img
                  :src="require('@/assets/images/svg/pie-chart.svg')"
                  style="width:100%; height:100%; object-fit: contain"
                />
              </div>
            </template>
          </vx-card>
        </vs-col>
        <!-- Programming Languages -->
        <vs-col vs-w="2.5" vs-xs="0">
          <vx-card>
            <h5 class="mb-4 text-center">Programming Languages</h5>
            <template v-if="languages.data.length > 0">
              <pie-chart
                style="height: 200px;"
                :chart-data="languages.data"
                chart-name="Programming Language"
                :key="CHART_KEYS.LANGUAGE_CHART_KEY"
              ></pie-chart>

              <p
                class="text-center text-muted"
                style="font-size: 8px;"
              >*Limited to top five documents based on your simulations statistics.</p>
            </template>
            <template v-else>
              <div class="block mx-auto my-3 text-center" style="height: 210px;">
                <img
                  :src="require('@/assets/images/svg/pie-chart.svg')"
                  style="width:100%; height:100%; object-fit: contain"
                />
              </div>
            </template>
          </vx-card>
        </vs-col>
        <!-- Attacks -->
        <vs-col vs-w="2.5" vs-xs="0">
          <vx-card>
            <h5 class="mb-4 text-center">Attacks</h5>
            <template v-if="attacks.data.length > 0">
              <pie-chart
                style="height: 200px;"
                :chart-data="attacks.data"
                chart-name="Attacks"
                :key="CHART_KEYS.ATTACK_CHART_KEY"
              ></pie-chart>
              <p
                class="text-center text-muted"
                style="font-size: 8px;"
              >*Based on your simulations statistics.</p>
            </template>
            <template v-else>
              <div class="block mx-auto my-3 text-center" style="height: 210px;">
                <img
                  :src="require('@/assets/images/svg/pie-chart.svg')"
                  style="width:100%; height:100%; object-fit: contain"
                />
              </div>
            </template>
          </vx-card>
        </vs-col>
        <!-- Activities -->
        <vs-col vs-w="3.9" vs-xs="0">
          <vx-card>
            <h5 class="mb-4 text-center">Activities</h5>
            <vue-perfect-scrollbar class="activity-area" v-if="activities.length > 0">
              <ul class="bordered-items">
                <li
                  v-for="ntf in activities"
                  :key="ntf._id"
                  class="flex justify-between px-4 py-4 cursor-pointer notification"
                >
                  <router-link :to="ntf.href" class="routerLink">
                    <div class="flex items-start">
                      <feather-icon
                        :icon="ntf.icon"
                        :svgClasses="[`text-${getColorByImportance(ntf.importance)}`, 'stroke-current mr-1 h-6 w-6']"
                      ></feather-icon>

                      <div class="mx-2">
                        <span
                          class="block font-medium notification-title"
                          :class="[`text-${getColorByImportance(ntf.importance)}`]"
                          style="text-transform: capitalize;"
                        >{{ ntf.title }}</span>
                        <small>{{ ntf.description }}</small>
                      </div>
                    </div>
                    <small
                      class="mt-1 whitespace-no-wrap"
                    >{{ ntf.createdAt | moment('from', 'now') }}</small>
                  </router-link>
                </li>
              </ul>
            </vue-perfect-scrollbar>
            <template v-else>
              <div class="block mx-auto my-3 text-center" style="height: 210px;">
                <img
                  :src="require('@/assets/images/svg/no-data.svg')"
                  style="width:100%; height:100%; object-fit: contain"
                />
                <p>Looks like there is no activity here...</p>
              </div>
            </template>
          </vx-card>
        </vs-col>
      </vs-row>
      <vs-row class="mt-2" vs-type="flex" vs-justify="space-between">
        <TrackList style="width: 100%;" :hideDeleteButton="true"></TrackList>
      </vs-row>
    </template>
    <template v-else>
      <vs-row vs-type="flex" vs-justify="center">
        <vs-col class="mt-20">
          <div class="block mx-auto my-3 text-center" style="height: 30vh;">
            <img
              :src="require('@/assets/images/svg/empty.svg')"
              style="width:100%; height:100%; object-fit: contain"
            />
            <h4 class="mt-4">Seems like you haven't created a simulation yet.</h4>
            <router-link :to="'/create'">
              <vs-button class="mt-4" variant="primary">Create One</vs-button>
            </router-link>
          </div>
        </vs-col>
      </vs-row>
    </template>
  </div>
</template>

<script>
import PieChart from './charts/PieChart.vue'
import RadialChart from './charts/RadialChart.vue'
import ColumnChart from './charts/ColumnChart.vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import TrackList from './track/TrackList.vue'
import eventBus from '../eventBus'
export default {
  components: {
    PieChart,
    RadialChart,
    ColumnChart,
    VuePerfectScrollbar,
    TrackList
  },
  data () {
    return {
      CHART_KEYS: {
        OS_CHART_KEY: 0,
        LANGUAGE_CHART_KEY: 0,
        ATTACK_CHART_KEY: 0
      },
      activities: [],
      simulations: 0,
      operatingSystems: {
        data: [],
        rawData: []
      },
      languages: {
        data: [],
        rawData: []
      },
      protections: [],
      attacks: {
        data: [],
        mitreAttacks: [],
        customAttacks: []
      }
    }
  },
  methods: {
    getColorByImportance (importance) {
      switch (importance) {
      case 1:
        return 'success'
      case 2:
        return 'warning'
      case 3:
        return 'danger'
      default:
        return 'primary'
      }
    },
    loadActivities (data) {
      this.activities = data
    },
    loadOperatingSystems (data) {
      const chartData = {
        data: [],
        rawData: data
      }
      data.forEach(os => {
        chartData.data.push({ name: os.name, value: os.count })
      })
      this.operatingSystems = chartData
      this.CHART_KEYS.OS_CHART_KEY++
    },
    loadLanguages (data) {
      const chartData = {
        data: [],
        rawData: data
      }
      data.forEach(language => {
        chartData.data.push({ name: language.name, value: language.count })
      })
      this.languages = chartData
      this.CHART_KEYS.LANGUAGE_CHART_KEY++
    },
    loadAttacks (customAttacks, mitreAttacks) {
      const chartData = {
        data: [],
        mitreAttacks,
        customAttacks
      }
      const reducer = (acc, cur) => acc + cur.count
      const mitreCount = mitreAttacks.reduce(reducer, 0)
      const customCount = customAttacks.reduce(reducer, 0)
      if (mitreCount > 0 || customCount > 0) {
        chartData.data = [
          { name: 'Mitre Att&ck', value: mitreCount },
          { name: 'Custom Attack', value: customCount }
        ]
      }
      this.attacks = chartData
      this.CHART_KEYS.ATTACK_CHART_KEY++
    },
    loadDashboard () {
      this.$http.get('/user/dashboard').then(res => {
        if (res.data.status && res.data.status === true) {
          this.loadActivities(res.data.results.activities.reverse())
          this.loadOperatingSystems(res.data.results.operatingSystems)
          this.loadLanguages(res.data.results.languages)
          this.loadAttacks(
            res.data.results.customAttacks,
            res.data.results.mitreAttacks
          )
          this.simulations = res.data.results.simulations
        }
      })
    }
  },
  created () {
    this.loadDashboard()
    eventBus.$on('table-data-deleted', () => {
      this.loadDashboard()
    })
  }
}
</script>
<style>
.activity-area {
  position: relative;
  margin: auto;
  height: 210px;
}
</style>